<template>
	<aside class="document-check-panel" role="dialog" aria-labelledby="document-check-title">
		<header class="document-check-header">
			<div>
				<h3 id="document-check-title">Document check</h3>
				<p class="document-check-summary" role="status">{{ summary }}</p>
			</div>
			<button type="button" class="document-check-close" aria-label="Close document check" @click="$emit('close')">
				<AppointmentIcon name="close" :size="16" />
			</button>
		</header>

		<div v-if="status === 'checking'" class="document-check-body document-check-loading">
			<span class="document-check-spinner" aria-hidden="true"></span>
			Checking spelling, grammar and wording…
		</div>

		<div v-else-if="status === 'error'" class="document-check-body document-check-error">
			<p>{{ error }}</p>
			<button type="button" class="document-check-btn document-check-btn--primary" @click="$emit('retry')">Try again</button>
		</div>

		<template v-else-if="status === 'done'">
			<ul v-if="visibleFindings.length" class="document-check-body document-check-list">
				<li v-for="finding in visibleFindings" :key="finding.id" class="document-check-item" :class="`is-${finding.state}`">
					<div class="document-check-meta">
						<span class="document-check-kind" :class="`kind-${finding.kind}`">{{ kindLabel(finding.kind) }}</span>
						<span class="document-check-place">{{ placeOf(finding) }}</span>
					</div>
					<p class="document-check-change">
						<span :class="finding.suggestion ? 'document-check-original' : 'document-check-highlight'">{{ finding.original }}</span>
						<template v-if="finding.suggestion">
							<span class="document-check-arrow" aria-hidden="true">→</span>
							<span class="document-check-suggestion">{{ finding.suggestion }}</span>
						</template>
					</p>
					<p v-if="finding.reason" class="document-check-reason">{{ finding.reason }}</p>
					<p v-if="finding.state === 'fixed'" class="document-check-state">Fixed</p>
					<p v-else-if="!stillApplies(finding)" class="document-check-state">This text has changed since the check.</p>
					<div v-if="finding.state === 'open'" class="document-check-actions">
						<template v-if="finding.suggestion && stillApplies(finding)">
							<button type="button" class="document-check-btn document-check-btn--primary" @click="$emit('fix', finding)">Fix</button>
							<button type="button" class="document-check-btn" @click="$emit('skip', finding)">Skip</button>
						</template>
						<button v-else type="button" class="document-check-btn" @click="$emit('skip', finding)">Got it</button>
					</div>
				</li>
			</ul>
			<p v-else class="document-check-body document-check-clean">
				{{ fieldsChecked ? `No issues found in ${fieldsChecked} text ${fieldsChecked === 1 ? 'field' : 'fields'}.` : 'There is no text to check yet.' }}
			</p>
			<footer v-if="fixableCount > 1" class="document-check-footer">
				<button type="button" class="document-check-btn document-check-btn--primary" @click="$emit('fix-all')">
					Fix all ({{ fixableCount }})
				</button>
			</footer>
		</template>
	</aside>
</template>

<script setup>
import { computed } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { FINDING_KIND_LABELS, describeFindingPlace, findingStillApplies } from '@/utils/documentCheck';

const props = defineProps({
	status: { type: String, default: 'idle' },
	// Each finding: { id, path, kind, original, suggestion, reason, state: 'open' | 'fixed' | 'skipped' }.
	findings: { type: Array, default: () => [] },
	fieldsChecked: { type: Number, default: 0 },
	error: { type: String, default: '' },
	// The document content the findings point into.
	content: { type: Object, default: () => ({}) },
});

defineEmits(['fix', 'skip', 'fix-all', 'retry', 'close']);

const stillApplies = (finding) => findingStillApplies(props.content, finding);
const kindLabel = (kind) => FINDING_KIND_LABELS[kind] || 'Wording';
const placeOf = (finding) => describeFindingPlace(props.content, finding.path);

// Skipped suggestions leave the list; fixed ones stay, marked, until the panel closes.
const visibleFindings = computed(() => props.findings.filter((finding) => finding.state !== 'skipped'));
const openFindings = computed(() => props.findings.filter((finding) => finding.state === 'open'));
const fixableCount = computed(
	() => openFindings.value.filter((finding) => finding.suggestion && stillApplies(finding)).length,
);
const fixedCount = computed(() => props.findings.filter((finding) => finding.state === 'fixed').length);

const summary = computed(() => {
	if (props.status === 'checking') return 'Reading the document…';
	if (props.status === 'error') return 'The check did not finish.';
	if (!props.findings.length) return 'All clear.';
	const open = openFindings.value.length;
	if (open) return `${open} ${open === 1 ? 'suggestion' : 'suggestions'} to review`;
	return fixedCount.value ? 'All done. Save the document to keep the fixes.' : 'All done.';
});
</script>

<style scoped>
.document-check-panel {
	position: fixed;
	z-index: 95;
	top: 72px;
	right: 24px;
	display: flex;
	flex-direction: column;
	width: min(380px, calc(100vw - 32px));
	/* Clear of the editor's action bar, also when it wraps onto a second row. */
	max-height: calc(100vh - 230px);
	overflow: hidden;
	border: 1px solid var(--border-color);
	border-radius: 12px;
	background: var(--noc-background, #fff);
	box-shadow:
		0 18px 42px rgba(15, 23, 42, 0.13),
		0 2px 8px rgba(15, 23, 42, 0.06);
	color: var(--text-color);
}

.document-check-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 14px 12px 16px;
	border-bottom: 1px solid var(--border-color);
}

.document-check-header h3 {
	margin: 0;
	font: 600 15px/20px var(--font-family-base);
}

.document-check-summary {
	margin: 2px 0 0;
	color: var(--text-color-secondary);
	font: 400 13px/18px var(--font-family-base);
}

.document-check-close {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary);
	cursor: pointer;
	touch-action: manipulation;
}

.document-check-body {
	margin: 0;
	padding: 14px 16px;
	font: 400 14px/20px var(--font-family-base);
}

.document-check-loading {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--text-color-secondary);
}

.document-check-spinner {
	width: 16px;
	height: 16px;
	flex: 0 0 auto;
	border: 2px solid var(--border-color);
	border-top-color: var(--noc-blue);
	border-radius: 50%;
	animation: document-check-spin 0.8s linear infinite;
}

.document-check-error p {
	margin: 0 0 12px;
	color: var(--noc-red);
}

.document-check-clean {
	color: var(--report-status-success-color);
}

.document-check-list {
	flex: 1 1 auto;
	overflow-y: auto;
	list-style: none;
	padding: 4px 0;
}

.document-check-item {
	padding: 12px 16px;
	border-bottom: 1px solid var(--border-color-secondary, #ebeef1);
}

.document-check-item:last-child {
	border-bottom: 0;
}

.document-check-item.is-fixed {
	opacity: 0.6;
}

.document-check-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	margin-bottom: 6px;
}

.document-check-kind {
	flex: 0 0 auto;
	padding: 1px 6px;
	border-radius: 4px;
	background: var(--report-status-muted-background);
	color: var(--report-status-muted-color);
	font: 600 11px/16px var(--font-family-base);
}

.document-check-kind.kind-spelling,
.document-check-kind.kind-grammar {
	background: var(--report-status-danger-background);
	color: var(--report-status-danger-color);
}

.document-check-kind.kind-consistency,
.document-check-kind.kind-placeholder {
	background: var(--report-status-warning-background);
	color: var(--report-status-warning-color);
}

.document-check-place {
	min-width: 0;
	overflow: hidden;
	color: var(--text-color-secondary);
	font: 400 12px/16px var(--font-family-base);
	text-overflow: ellipsis;
	white-space: nowrap;
}

.document-check-change {
	margin: 0;
	overflow-wrap: anywhere;
}

.document-check-original {
	color: var(--report-status-danger-color);
	text-decoration: line-through;
}

.document-check-highlight {
	padding: 0 2px;
	border-radius: 3px;
	background: var(--report-status-warning-background);
}

.document-check-arrow {
	margin: 0 6px;
	color: var(--text-color-secondary);
}

.document-check-suggestion {
	color: var(--report-status-success-color);
	font-weight: 600;
}

.document-check-reason,
.document-check-state {
	margin: 4px 0 0;
	color: var(--text-color-secondary);
	font: 400 13px/18px var(--font-family-base);
}

.document-check-actions {
	display: flex;
	gap: 8px;
	margin-top: 10px;
}

.document-check-btn {
	height: 30px;
	padding: 0 12px;
	border: 1px solid var(--border-color);
	border-radius: 6px;
	background: var(--noc-background, #fff);
	color: var(--text-color);
	font: 500 13px/1 var(--font-family-base);
	cursor: pointer;
	touch-action: manipulation;
}

.document-check-btn--primary {
	border-color: var(--noc-blue);
	background: var(--noc-blue);
	color: #fff;
}

.document-check-footer {
	display: flex;
	justify-content: flex-end;
	padding: 10px 16px;
	border-top: 1px solid var(--border-color);
}

@keyframes document-check-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (hover: hover) {
	.document-check-close:hover,
	.document-check-btn:not(.document-check-btn--primary):hover {
		background: var(--noc-background-secondary);
	}

	.document-check-btn--primary:hover {
		background: var(--primary-hover, #0855b3);
	}
}

/* The editor's action bar spans the width here and wraps onto two or three rows; the panel
   stays at the top and stops short of it. */
@media (max-width: 980px) {
	.document-check-panel {
		right: 16px;
		max-height: calc(100vh - 272px);
	}
}

@media (prefers-reduced-motion: reduce) {
	.document-check-spinner {
		animation: none;
	}
}
</style>
